import { useForm, getFormProps } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { json, type ActionFunctionArgs } from '@remix-run/node'
import { useFetcher, useFetchers } from '@remix-run/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
	defaultLanguage,
	supportedLanguages,
	type Language,
} from '#app/utils/i18n.ts'
import { localeCookie } from '#app/utils/locale.server.ts'

const LocaleFormSchema = z.object({
	locale: z.enum(supportedLanguages),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: LocaleFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid locale received')

	const { locale } = submission.value

	return json(
		{ result: submission.reply() },
		{
			headers: {
				'Set-Cookie': await localeCookie.serialize(locale),
			},
		},
	)
}

export function LanguageSwitch({
	userPreference,
}: {
	userPreference: string
}) {
	const fetcher = useFetcher<typeof action>()
	const { t, i18n } = useTranslation()

	const [form] = useForm({
		id: 'locale-switch',
		lastResult: fetcher.data?.result,
	})

	const optimisticLocale = useOptimisticLocale()
	const locale = (optimisticLocale ??
		userPreference ??
		defaultLanguage) as Language
	const nextLocale: Language = locale === 'en' ? 'pt' : 'en'
	const nextLabel = t(`language.${nextLocale}`)

	// Apply the pending language immediately so copy updates before revalidation.
	useEffect(() => {
		if (optimisticLocale && i18n.language !== optimisticLocale) {
			void i18n.changeLanguage(optimisticLocale)
		}
	}, [optimisticLocale, i18n])

	return (
		<fetcher.Form
			method="POST"
			{...getFormProps(form)}
			action="/resources/locale-switch"
		>
			<input type="hidden" name="locale" value={nextLocale} />
			<button
				type="submit"
				className="flex h-8 cursor-pointer items-center justify-center rounded-sm px-2 text-xs font-semibold uppercase tracking-wide text-muted-700 hover:text-foreground"
				aria-label={t('language.switchTo', { language: nextLabel })}
				title={t('language.switchTo', { language: nextLabel })}
			>
				{nextLocale}
			</button>
		</fetcher.Form>
	)
}

/**
 * If the user is changing language, return the value it's being changed to.
 */
export function useOptimisticLocale() {
	const fetchers = useFetchers()
	const localeFetcher = fetchers.find(
		f => f.formAction === '/resources/locale-switch',
	)

	if (localeFetcher?.formData) {
		const submission = parseWithZod(localeFetcher.formData, {
			schema: LocaleFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.locale
		}
	}
}
